import { coerceUserConfigValue } from "../../contexts/AuthContext";
import type { User } from "../../services/usersService";
import type { UserConfiguration } from "../../services/UserConfigurationService";
import type { UserSettingsMap } from "../../services/profileConfigService";
import type { AlphaIbkrAccountType } from "./alphaIbkrAccountDetails";
import type { AlphaProgressResponses } from "./alphaProgressResponses";

export interface VerifiedAdminTargetSubject {
  userId: string;
  user: User;
  userConfig: UserConfiguration;
  generation: number;
}

export type TargetAccountState =
  | { status: "loading"; ownerUserId: string; generation: number }
  | { status: "linked"; ownerUserId: string; generation: number }
  | { status: "none"; ownerUserId: string; generation: number }
  | { status: "failed"; ownerUserId: string; generation: number };

export type TargetQuestionnaireState =
  | { status: "loading"; ownerUserId: string; generation: number }
  | {
      status: "loaded";
      ownerUserId: string;
      generation: number;
      progressResponses: AlphaProgressResponses;
    }
  | { status: "failed"; ownerUserId: string; generation: number };

export type AlphaDashboardPreviewPageProps =
  | {
      mode: "self";
      progressResponses: AlphaProgressResponses | null;
      effectiveAccountType: AlphaIbkrAccountType | null;
      isPersistenceLoading: boolean;
      persistenceLoadFailed: boolean;
      onRetryPersistence?: () => void;
      isReviewCompleted?: boolean;
      isReviewCompletionLoading?: boolean;
      reviewCompletionLoadFailed?: boolean;
      onRetryReviewCompletion?: () => void;
      requiresAccountTypeReview?: boolean;
      setupV2ReadyOwner?: string;
    }
  | {
      mode: "admin-target";
      subject: VerifiedAdminTargetSubject;
      questionnaire: TargetQuestionnaireState;
      accountState: TargetAccountState;
      onRetryQuestionnaire: () => void;
      onRetryAccountPreflight: () => void;
    };

export interface AdminTargetPositionsAdapterProps {
  subject: VerifiedAdminTargetSubject;
  accountState: TargetAccountState;
  onRetryAccountPreflight: () => void;
}

export type AlphaProfilePageProps =
  | {
      mode: "self";
      progressResponses: AlphaProgressResponses | null;
      isPersistenceLoading?: boolean;
      persistenceLoadFailed?: boolean;
      onRetryPersistence?: () => void;
      isReviewCompleted?: boolean;
      isBrokerageProvisioned?: boolean;
    }
  | {
      mode: "admin-target";
      subject: VerifiedAdminTargetSubject;
      questionnaire: TargetQuestionnaireState;
      onRetryQuestionnaire: () => void;
    };

export function getTargetConfigValue(
  configuration: Pick<UserConfiguration, "data">,
  key: string
): string | null {
  return coerceUserConfigValue(configuration.data?.[key]);
}

export function getTargetUserSettings(
  configuration: Pick<UserConfiguration, "data">
): UserSettingsMap | undefined {
  const raw = configuration.data?.userSettings;
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    return raw as UserSettingsMap;
  }
  return undefined;
}
